import { useEffect, useState } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
  Legend,
} from "recharts";
import { PageHeader } from "../components/Shell";
import { api, currency, FUEL_LABELS, FUEL_COLORS } from "../lib/api";
import { useStation } from "../lib/station";

export default function PL() {
  const { activeId } = useStation();
  const [data, setData] = useState({ trend: [], by_type: [] });
  const [days, setDays] = useState(7);

  useEffect(() => {
    if (!activeId) return;
    api.plSummary(activeId, days).then(setData);
  }, [days, activeId]);

  if (!activeId) return null;

  const totalRev = data.trend.reduce((a, d) => a + d.revenue, 0);
  const totalCost = data.trend.reduce((a, d) => a + d.cost, 0);
  const totalProfit = totalRev - totalCost;
  const margin = totalRev > 0 ? (totalProfit / totalRev) * 100 : 0;

  return (
    <div>
      <PageHeader
        title="P&L"
        subtitle="Profit & loss snapshot"
        action={
          <div className="flex gap-1 surface rounded-lg p-1">
            {[7, 14, 30].map((d) => (
              <button
                key={d}
                onClick={() => setDays(d)}
                data-testid={`pl-range-${d}`}
                className={`h-9 px-3 rounded-md text-xs font-bold uppercase tracking-wider ${
                  days === d ? "bg-[#FF6B00] text-white" : "text-gray-400 hover:text-white"
                }`}
              >
                {d}d
              </button>
            ))}
          </div>
        }
      />

      <div className="grid grid-cols-3 gap-3 mb-5" data-testid="pl-totals">
        <Tot label="Revenue" value={currency(totalRev)} />
        <Tot label="Cost" value={currency(totalCost)} />
        <Tot
          label="Profit"
          value={currency(totalProfit)}
          accent={totalProfit >= 0 ? "#10B981" : "#EF4444"}
          sub={`${margin.toFixed(1)}% margin`}
        />
      </div>

      <h2 className="font-display font-bold uppercase tracking-wider text-sm text-gray-400 mb-3">Daily Margin</h2>
      <div className="surface rounded-lg p-3 mb-6" data-testid="pl-chart">
        <ResponsiveContainer width="100%" height={240}>
          <BarChart data={data.trend} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" vertical={false} />
            <XAxis
              dataKey="date"
              tick={{ fill: "#6B7280", fontSize: 10 }}
              tickFormatter={(v) => v.slice(5)}
              axisLine={{ stroke: "rgba(255,255,255,0.08)" }}
              tickLine={false}
            />
            <YAxis
              tick={{ fill: "#6B7280", fontSize: 10 }}
              axisLine={{ stroke: "rgba(255,255,255,0.08)" }}
              tickLine={false}
              tickFormatter={(v) => `$${v / 1000}k`}
            />
            <Tooltip
              contentStyle={{
                background: "#1A1D24",
                border: "1px solid rgba(255,255,255,0.08)",
                borderRadius: 8,
                color: "#F9FAFB",
              }}
              labelStyle={{ color: "#9CA3AF" }}
              formatter={(v, name) => [currency(v), name]}
            />
            <Legend wrapperStyle={{ fontSize: 11, paddingTop: 8 }} />
            <Bar dataKey="cost" name="Cost" fill="#374151" radius={[4, 4, 0, 0]} />
            <Bar dataKey="profit" name="Profit" fill="#FF6B00" radius={[4, 4, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>

      <h2 className="font-display font-bold uppercase tracking-wider text-sm text-gray-400 mb-3">By Fuel Type</h2>
      <div className="grid gap-3">
        {data.by_type.map((b) => (
          <div key={b.fuel_type} className="surface rounded-lg p-4" data-testid={`pl-type-${b.fuel_type}`}>
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <span className="h-3 w-3 rounded-full" style={{ background: FUEL_COLORS[b.fuel_type] }} />
                <span className="text-sm font-bold uppercase tracking-wider text-white">{FUEL_LABELS[b.fuel_type]}</span>
              </div>
              <span className="font-display font-black text-2xl" style={{ color: FUEL_COLORS[b.fuel_type] }}>
                {b.margin_pct.toFixed(1)}%
              </span>
            </div>
            <div className="grid grid-cols-4 gap-2 text-xs">
              <Cell label="Gallons" v={`${b.gallons.toLocaleString()}`} />
              <Cell label="Revenue" v={currency(b.revenue)} />
              <Cell label="Cost" v={currency(b.cost)} />
              <Cell label="Profit" v={currency(b.profit)} accent={b.profit >= 0 ? "#10B981" : "#EF4444"} />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function Tot({ label, value, sub, accent }) {
  return (
    <div className="surface rounded-lg p-3">
      <div className="text-[10px] font-bold uppercase tracking-[0.16em] text-gray-500">{label}</div>
      <div className="font-display font-black text-xl mt-1" style={accent ? { color: accent } : undefined}>
        {value}
      </div>
      {sub && <div className="text-[10px] text-gray-500 mt-0.5">{sub}</div>}
    </div>
  );
}

function Cell({ label, v, accent }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-gray-500">{label}</div>
      <div className="font-mono text-sm" style={accent ? { color: accent } : undefined}>
        {v}
      </div>
    </div>
  );
}
